import { CalendarTodayOutlined, DescriptionOutlined, LocalOfferOutlined } from "@mui/icons-material";
import { Box, Switch, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import SoftInput from "components/SoftInput";
import Campo from "../Campo";
import { DURACION_MAXIMA_EVENTO_DIAS, ahoraInput, duracionTexto } from "../../utils";

const iconoSx = {
  display: "grid", placeItems: "center", width: 30, height: 30, flexShrink: 0, borderRadius: "10px",
  color: "#6034F2", background: "linear-gradient(135deg, #EEE9FF, #E5DEFF)", "& svg": { fontSize: 19 },
};
const columnaSx = {
  display: "grid", gridTemplateColumns: "30px minmax(0, 1fr)", alignItems: "start", columnGap: 1,
  minWidth: 0,
  "& .MuiInputLabel-root > .MuiTypography-root": { color: "#1C173C", fontSize: 12.5 },
  "& .MuiInputBase-root": { minHeight: 36, borderRadius: "10px", borderColor: "#D4D5E5", boxShadow: "none" },
  "& .MuiInputBase-root.Mui-focused:not(.evento-dato-error)": { borderColor: "#6530FF", boxShadow: "0 0 0 1px #6530FF" },
  "& .MuiInputBase-root.evento-dato-error": { borderColor: "#E84B65" },
  "& .MuiInputBase-root.evento-dato-error.Mui-focused": { boxShadow: "0 0 0 1px #E84B65" },
  "& .MuiInputBase-root.Mui-disabled": { backgroundColor: "#F0F1F6 !important" },
  "& .MuiInputBase-input": { fontSize: 13, minWidth: 0 },
  "& input.Mui-disabled": { WebkitTextFillColor: "#465063" },
};
const divisor = "1px solid #E0D9FF";

export default function PasoDatos({ form, setField, errores, inicioBloqueado, soloLectura, finEditable }) {
  const duracion = duracionTexto(form.inicio, form.fin);
  // Al extender el cierre de un finalizado solo el Fin se edita, sin tope de días.
  const finBloqueado = soloLectura && !finEditable;
  // El mínimo se recalcula al enfocar: la pantalla puede quedar abierta un rato.
  const [minFin, setMinFin] = useState(ahoraInput);
  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      <Box sx={{
        display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "minmax(0, 1.45fr) repeat(2, minmax(0, 1fr))" },
        rowGap: { xs: 1.5, lg: 0 }, p: { xs: 1.5, sm: 1.75 }, border: "1px solid #E9E3FF", borderRadius: "14px",
        background: "linear-gradient(110deg, #FAF8FF, #F7F6FD)",
      }}>
      <Box sx={{ ...columnaSx, gridColumn: { sm: "1 / -1", lg: "auto" }, pr: { lg: 2.5 } }}>
        <Box sx={iconoSx}><LocalOfferOutlined /></Box>
        <Box minWidth={0}>
        <Campo id="evento-nombre" label="Nombre del evento" requerido error={errores.nombre}>
          <SoftInput id="evento-nombre" placeholder="Ej.: CONAFO 2026" value={form.nombre} className={errores.nombre ? "evento-dato-error" : undefined}
            onChange={(e) => setField("nombre", e.target.value)} error={Boolean(errores.nombre)} disabled={soloLectura}
            inputProps={{ maxLength: 255, disabled: soloLectura, "aria-invalid": Boolean(errores.nombre) }} autoFocus />
        </Campo>
        </Box>
      </Box>
      <Box sx={{ ...columnaSx, borderTop: { xs: divisor, lg: 0 }, pt: { xs: 1.5, lg: 0 }, borderLeft: { lg: divisor }, pl: { lg: 2.5 }, pr: { sm: 2.5 } }}>
        <Box sx={iconoSx}><CalendarTodayOutlined /></Box>
        <Box minWidth={0}>
        <Campo id="evento-inicio" label="Inicio" requerido error={errores.inicio}
          ayuda={inicioBloqueado && !soloLectura ? "Fijo: el evento ya comenzó o tiene ventas." : null}>
          <SoftInput id="evento-inicio" type="datetime-local" value={form.inicio} disabled={inicioBloqueado || soloLectura} className={errores.inicio ? "evento-dato-error" : undefined}
            onChange={(e) => setField("inicio", e.target.value)} error={Boolean(errores.inicio)}
            inputProps={{ disabled: inicioBloqueado || soloLectura, "aria-invalid": Boolean(errores.inicio) }} />
        </Campo>
        </Box>
      </Box>
      <Box sx={{ ...columnaSx, borderTop: { xs: divisor, lg: 0 }, pt: { xs: 1.5, lg: 0 }, borderLeft: { sm: divisor }, pl: { sm: 2.5 } }}>
        <Box sx={iconoSx}><CalendarTodayOutlined /></Box>
        <Box minWidth={0}>
        <Campo id="evento-fin" label="Fin" requerido error={errores.fin}
          ayuda={errores.fin || !duracion ? null
            : finEditable ? `Duración: ${duracion}. Nuevo cierre posterior a ahora, sin límite de duración.`
              : `Duración: ${duracion} (máximo ${DURACION_MAXIMA_EVENTO_DIAS} días)`}>
          <SoftInput id="evento-fin" type="datetime-local" value={form.fin} disabled={finBloqueado} className={errores.fin ? "evento-dato-error" : undefined}
            onFocus={finEditable ? () => setMinFin(ahoraInput()) : undefined}
            onChange={(e) => setField("fin", e.target.value)} error={Boolean(errores.fin)}
            inputProps={{ disabled: finBloqueado, "aria-invalid": Boolean(errores.fin), ...(finEditable ? { min: minFin } : {}) }} />
        </Campo>
        </Box>
      </Box>
      </Box>
      {inicioBloqueado && !soloLectura && (
        <Box role="note" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 1.5, py: 1.25, borderRadius: "12px", background: "#F5F3FD" }}>
          <Box sx={{ ...iconoSx, width: 36, height: 36 }}><DescriptionOutlined sx={{ fontSize: "22px !important" }} /></Box>
          <Typography sx={{ fontSize: 12.5, lineHeight: "1.5 !important", color: "#62699F", alignSelf: "center" }}>
            Como el evento ya comenzó o tiene ventas, el inicio no se puede cambiar. El fin puede adelantarse o
            extenderse, sin quedar antes de ahora ni de la última venta. Precios, productos, medios de pago, impresora y
            bloqueos sí se pueden cambiar y aplican a las ventas siguientes.
          </Typography>
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pt: 0.75 }}>
        <Switch checked={form.activo} disabled={soloLectura} onChange={(_, value) => setField("activo", value)}
          inputProps={{ id: "evento-activo", "aria-label": "Evento activo" }} sx={{
            width: 48, height: 24,
            "& .MuiSwitch-thumb": { width: 20, height: 20, boxShadow: "0 1px 3px rgba(0,0,0,.15)" },
            "& .MuiSwitch-switchBase": { transform: "translate(2px, -50%)" },
            "& .MuiSwitch-switchBase.Mui-checked": { transform: "translate(26px, -50%)" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#00A344 !important", borderColor: "#00A344 !important" },
          }} />
        <Typography component="label" htmlFor="evento-activo" sx={{ fontSize: 13, fontWeight: 700, color: "#191535", cursor: soloLectura ? "default" : "pointer" }}>
          {form.activo ? "Evento activo" : "Evento desactivado"}
        </Typography>
      </Box>
    </Box>
  );
}

PasoDatos.propTypes = {
  form: PropTypes.object.isRequired,
  setField: PropTypes.func.isRequired,
  errores: PropTypes.object.isRequired,
  inicioBloqueado: PropTypes.bool,
  soloLectura: PropTypes.bool,
  finEditable: PropTypes.bool,
};

PasoDatos.defaultProps = { inicioBloqueado: false, soloLectura: false, finEditable: false };
